import { GemSalesTracker } from "./tracker";
import { FlowConfig, FlowResult, GemSalesConfig, GeneratedExperience, ShopperContext, TriggerConfig } from "./types";
import { incrementTriggerImpressionCount, persistDismissedSlots, persistRecommendationAttribution } from "./storage";
import { buildTokenCss } from "./ui-tokens";
import { answerFollowUpQuestion, sanitizeFlowResult } from "./guardrails";
import { EXPERIENCE_TEMPLATES, buildGeneratedExperience } from "./experiences";
import { renderExperienceSectionsHtml } from "./experience-renderer";
import { buildExperiencePageUrl } from "./experience-page-request";

export class GemSalesRenderer {
  private host: HTMLElement | null = null;
  private root: ShadowRoot | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(
    private readonly config: GemSalesConfig,
    private readonly context: ShopperContext,
    private readonly tracker: GemSalesTracker,
  ) {}

  renderTrigger(trigger: TriggerConfig): void {
    if (this.host) return;

    const anchor = findAnchor(trigger);
    if (!anchor) return;

    this.host = document.createElement("div");
    this.host.setAttribute("data-gemsales-slot", trigger.id);
    this.host.setAttribute("data-gemsales-runtime-slot", trigger.runtimeSlotId);
    this.root = this.host.attachShadow({ mode: "open" });
    this.root.innerHTML = this.buildTriggerHtml(trigger);

    if (isReservedSlot(anchor, trigger)) {
      anchor.replaceChildren(this.host);
    } else {
      anchor.insertAdjacentElement(trigger.surface === "cart" ? "afterbegin" : "afterend", this.host);
    }

    this.root.querySelector<HTMLButtonElement>("[data-action='open']")?.addEventListener("click", () => {
      this.openFlowFromTrigger(trigger, "primary_cta");
    });

    this.root.querySelectorAll<HTMLButtonElement>("[data-quick-reply]").forEach((button) => {
      button.addEventListener("click", () => {
        const quickReply = button.dataset.quickReply;
        this.openFlowFromTrigger(trigger, "quick_reply", quickReply);
      });
    });

    this.root.querySelector<HTMLButtonElement>("[data-action='dismiss']")?.addEventListener("click", () => {
      this.context.dismissedSlotIds.add(trigger.id);
      persistDismissedSlots(this.context.dismissedSlotIds);
      this.tracker.track("gemsales.trigger.dismiss", { slot_id: trigger.id });
      this.host?.remove();
      this.host = null;
      this.root = null;
    });

    const impressionCount = incrementTriggerImpressionCount(trigger.id, this.context.triggerImpressionCounts);

    this.tracker.track("gemsales.trigger.impression", {
      slot_id: trigger.id,
      runtime_slot_id: trigger.runtimeSlotId,
      flow_id: trigger.flowId,
      question: trigger.question,
      surface: trigger.surface,
      impression_count: impressionCount,
      max_impressions_per_session: trigger.maxImpressionsPerSession ?? null,
    });
  }

  destroy(): void {
    this.unbindKeyboard();
    this.host?.remove();
    this.host = null;
    this.root = null;
  }

  private renderFlow(trigger: TriggerConfig): void {
    const flow = this.config.flows.find((candidate) => candidate.id === trigger.flowId);
    if (!flow || !this.root) return;

    const answers: Record<string, string> = {};
    let stepIndex = 0;
    this.tracker.track("gemsales.flow.start", { flow_id: flow.id, slot_id: trigger.id });

    const renderStep = () => {
      const step = flow.steps[stepIndex];
      if (!step) {
        this.renderResult(flow, answers);
        return;
      }

      this.root!.innerHTML = this.buildFlowStepHtml(flow, stepIndex);
      this.bindCloseButton();
      this.bindKeyboard();
      this.root!.querySelectorAll<HTMLButtonElement>("[data-option-id]").forEach((button) => {
        button.addEventListener("click", () => {
          const answer = button.dataset.optionId;
          if (!answer) return;

          answers[step.id] = answer;
          this.tracker.track("gemsales.flow.answer", {
            flow_id: flow.id,
            step_id: step.id,
            answer_key: answer,
          });
          stepIndex += 1;
          renderStep();
        });
      });
      this.focusFirstInteractive();
    };

    renderStep();
  }

  private openFlowFromTrigger(trigger: TriggerConfig, entrySurface: "primary_cta" | "quick_reply", quickReply?: string): void {
    this.tracker.track("gemsales.trigger.click", {
      slot_id: trigger.id,
      runtime_slot_id: trigger.runtimeSlotId,
      flow_id: trigger.flowId,
      question: trigger.question,
      entry_surface: entrySurface,
      quick_reply: quickReply ?? null,
    });

    if (quickReply) {
      this.tracker.track("gemsales.quick_reply.click", {
        slot_id: trigger.id,
        runtime_slot_id: trigger.runtimeSlotId,
        flow_id: trigger.flowId,
        quick_reply: quickReply,
      });
    }

    this.renderFlow(trigger);
  }

  private renderResult(flow: FlowConfig, answers: Record<string, string>): void {
    if (!this.root) return;

    const result = sanitizeFlowResult(flow.resolveResult(answers, this.config), this.config);
    const experience = buildGeneratedExperience(flow, answers, result, this.config);
    const guideUrl = experience && supportsDynamicPage(experience) ? buildExperienceGuideUrl(flow.id, answers) : null;
    const recommendationId = `rec_${crypto.randomUUID()}`;
    this.root.innerHTML = this.buildResultHtml(result, experience, guideUrl);
    this.bindCloseButton();
    this.bindKeyboard();
    this.tracker.track("gemsales.flow.complete", {
      flow_id: flow.id,
      result_title: result.title,
      template_id: experience?.templateId ?? result.templateId ?? flow.templateId ?? null,
      answers,
    });
    this.tracker.track("gemsales.recommendation.view", {
      recommendation_id: recommendationId,
      flow_id: flow.id,
      result_title: result.title,
      recommended_product_handle: result.recommendedProductHandle ?? null,
    });
    if (experience) {
      this.tracker.track("gemsales.experience.view", {
        experience_id: experience.id,
        template_id: experience.templateId,
        surface: experience.surface,
        section_count: experience.sections.length,
        recommended_product_handle: experience.recommendedProductHandle ?? null,
      });
    }

    this.root.querySelector<HTMLAnchorElement>("[data-action='cta']")?.addEventListener("click", () => {
      persistRecommendationAttribution({
        recommendationId,
        flowId: flow.id,
        resultTitle: result.title,
        ctaUrl: result.ctaUrl ?? null,
        recommendedProductHandle: result.recommendedProductHandle ?? null,
        clickedAt: Date.now(),
      });
      this.tracker.track("gemsales.recommendation.click", {
        recommendation_id: recommendationId,
        flow_id: flow.id,
        cta_url: result.ctaUrl,
        result_title: result.title,
        recommended_product_handle: result.recommendedProductHandle ?? null,
      });
    });
    this.root.querySelectorAll<HTMLElement>("[data-experience-section]").forEach((section) => {
      section.addEventListener("click", (event) => {
        const target = event.target as HTMLElement | null;
        const sectionType = section.dataset.experienceSection;
        if (!sectionType || !target?.closest("a,button")) return;

        this.tracker.track("gemsales.experience.section.click", {
          experience_id: experience?.id ?? null,
          template_id: experience?.templateId ?? null,
          section_type: sectionType,
        });
      });
    });
    this.root.querySelector<HTMLAnchorElement>("[data-action='open-guide']")?.addEventListener("click", () => {
      this.tracker.track("gemsales.experience.guide.click", {
        experience_id: experience?.id ?? null,
        template_id: experience?.templateId ?? null,
        flow_id: flow.id,
        guide_url: guideUrl,
      });
    });
    this.bindFollowUpForm(flow);
    this.focusFirstInteractive();
  }

  private bindFollowUpForm(flow: FlowConfig): void {
    const form = this.root?.querySelector<HTMLFormElement>("[data-followup-form]");
    if (!form) return;

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const input = form.querySelector<HTMLInputElement>("[data-followup-input]");
      const output = this.root?.querySelector<HTMLElement>("[data-followup-answer]");
      const question = input?.value.trim() ?? "";
      if (!question || !input || !output) return;

      const response = answerFollowUpQuestion(question, this.config);
      output.hidden = false;
      output.innerHTML = `
        <p class="answer-label">${escapeHtml(formatTopic(response.topic))}</p>
        <p>${escapeHtml(response.answer)}</p>
        ${response.caution ? `<p class="caution">${escapeHtml(response.caution)}</p>` : ""}
      `;
      this.tracker.track("gemsales.followup.submit", {
        flow_id: flow.id,
        question,
        answer_topic: response.topic,
      });
      this.tracker.track("gemsales.followup.answer", {
        flow_id: flow.id,
        answer_topic: response.topic,
        used_approved_content: true,
      });
      input.value = "";
      output.focus();
    });
  }

  private closePanel(): void {
    this.tracker.track("gemsales.drawer.close");
    this.destroy();
  }

  private bindCloseButton(): void {
    this.root?.querySelector<HTMLButtonElement>("[data-action='close-panel']")?.addEventListener("click", () => {
      this.closePanel();
    });
  }

  private bindKeyboard(): void {
    if (this.keydownHandler) return;

    this.keydownHandler = (event) => {
      if (event.key === "Escape") this.closePanel();
    };
    document.addEventListener("keydown", this.keydownHandler);
  }

  private unbindKeyboard(): void {
    if (!this.keydownHandler) return;

    document.removeEventListener("keydown", this.keydownHandler);
    this.keydownHandler = null;
  }

  private focusFirstInteractive(): void {
    window.setTimeout(() => {
      this.root?.querySelector<HTMLElement>("[data-option-id], [data-action='cta'], .primary, button, a")?.focus();
    }, 0);
  }

  private buildTriggerHtml(trigger: TriggerConfig): string {
    return `
      ${this.styles()}
      <article class="card trigger">
        <div class="header-row">
          <span class="brand-mark" aria-hidden="true">G</span>
          <div>
            <p class="eyebrow">${escapeHtml(this.config.brandName)} assistant</p>
            <h3>${escapeHtml(trigger.question)}</h3>
          </div>
          <button class="icon-button" data-action="dismiss" aria-label="Dismiss GemSales prompt">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5.75 5.75a.8.8 0 0 1 1.13 0L10 8.87l3.12-3.12a.8.8 0 1 1 1.13 1.13L11.13 10l3.12 3.12a.8.8 0 1 1-1.13 1.13L10 11.13l-3.12 3.12a.8.8 0 0 1-1.13-1.13L8.87 10 5.75 6.88a.8.8 0 0 1 0-1.13Z"/></svg>
          </button>
        </div>
        ${
          trigger.quickReplies?.length
            ? `<div class="chips">${trigger.quickReplies
                .map(
                  (reply) =>
                    `<button type="button" data-quick-reply="${escapeHtml(reply)}" aria-label="Start with ${escapeHtml(reply)}">${escapeHtml(reply)}</button>`,
                )
                .join("")}</div>`
            : ""
        }
        <button class="primary" data-action="open">
          <span>Start</span>
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M11.3 4.3a1 1 0 0 1 1.4 0l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 1 1-1.4-1.4L14.6 11H3a1 1 0 1 1 0-2h11.6l-3.3-3.3a1 1 0 0 1 0-1.4Z"/></svg>
        </button>
      </article>
    `;
  }

  private buildFlowStepHtml(flow: FlowConfig, stepIndex: number): string {
    const step = flow.steps[stepIndex];
    if (!step) return "";

    return `
      ${this.styles()}
      <article class="card drawer" role="dialog" aria-modal="false" aria-label="${escapeHtml(flow.title)}">
        <div class="panel-topline">
          <span class="brand-mark" aria-hidden="true">G</span>
          <p class="eyebrow">${escapeHtml(flow.title)}</p>
          <span class="step-count">${stepIndex + 1}/${flow.steps.length}</span>
          <button class="icon-button" data-action="close-panel" aria-label="Close GemSales panel">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5.75 5.75a.8.8 0 0 1 1.13 0L10 8.87l3.12-3.12a.8.8 0 1 1 1.13 1.13L11.13 10l3.12 3.12a.8.8 0 1 1-1.13 1.13L10 11.13l-3.12 3.12a.8.8 0 0 1-1.13-1.13L8.87 10 5.75 6.88a.8.8 0 0 1 0-1.13Z"/></svg>
          </button>
        </div>
        <h3>${escapeHtml(step.question)}</h3>
        <div class="options">
          ${step.options
            .map((option) => `<button class="option" data-option-id="${escapeHtml(option.id)}">${escapeHtml(option.label)}</button>`)
            .join("")}
        </div>
      </article>
    `;
  }

  private buildResultHtml(result: FlowResult, experience: GeneratedExperience | null, guideUrl: string | null): string {
    return `
      ${this.styles()}
      <article class="card drawer" role="dialog" aria-modal="false" aria-label="GemSales recommendation">
        <div class="panel-topline">
          <span class="brand-mark" aria-hidden="true">G</span>
          <p class="eyebrow">Recommendation</p>
          <button class="icon-button" data-action="close-panel" aria-label="Close GemSales panel">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5.75 5.75a.8.8 0 0 1 1.13 0L10 8.87l3.12-3.12a.8.8 0 1 1 1.13 1.13L11.13 10l3.12 3.12a.8.8 0 1 1-1.13 1.13L10 11.13l-3.12 3.12a.8.8 0 0 1-1.13-1.13L8.87 10 5.75 6.88a.8.8 0 0 1 0-1.13Z"/></svg>
          </button>
        </div>
        <h3>${escapeHtml(result.title)}</h3>
        ${
          experience
            ? `<div class="experience" data-experience-id="${escapeHtml(experience.id)}">${renderExperienceSectionsHtml(experience, this.config)}</div>`
            : `<p>${escapeHtml(result.body)}</p>${result.caution ? `<p class="caution">${escapeHtml(result.caution)}</p>` : ""}`
        }
        ${result.ctaUrl ? `<a class="primary link" data-action="cta" href="${escapeHtml(result.ctaUrl)}">${escapeHtml(result.ctaLabel)}</a>` : ""}
        ${
          result.secondaryUrl && result.secondaryLabel
            ? `<a class="secondary" href="${escapeHtml(result.secondaryUrl)}">${escapeHtml(result.secondaryLabel)}</a>`
            : ""
        }
        ${guideUrl ? `<a class="secondary guide-link" data-action="open-guide" href="${escapeHtml(guideUrl)}">Open full guide</a>` : ""}
        <form class="followup" data-followup-form>
          <label for="gemsales-followup">Ask a follow-up</label>
          <div class="followup-row">
            <input id="gemsales-followup" data-followup-input type="text" placeholder="Ask about proof, safety, taste, or value" />
            <button type="submit" aria-label="Ask GemSales">
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M2.8 3.5a1 1 0 0 1 1.05-.1l13 6a1 1 0 0 1 0 1.8l-13 6A1 1 0 0 1 2.45 16l2.1-5.25H10a.75.75 0 0 0 0-1.5H4.55L2.45 4a1 1 0 0 1 .35-.5Z"/></svg>
            </button>
          </div>
          <div class="followup-answer" data-followup-answer tabindex="-1" hidden></div>
        </form>
      </article>
    `;
  }

  private styles(): string {
    return `
      <style>
        :host { ${buildTokenCss(this.config)} all: initial; color: var(--gem-color-text); font-family: var(--gem-font-body); }
        * { box-sizing: border-box; }
        .card { width: min(100%, 428px); margin: var(--gem-space-4) auto; padding: var(--gem-space-4); border: 1px solid var(--gem-color-border); border-radius: var(--gem-radius-md); background: var(--gem-color-surface); color: var(--gem-color-text); box-shadow: var(--gem-shadow-sm); position: relative; }
        .trigger { border-left: 3px solid var(--gem-color-primary); }
        .drawer { position: fixed; right: var(--gem-space-4); bottom: var(--gem-space-4); z-index: 2147483000; max-width: calc(100vw - 32px); box-shadow: var(--gem-shadow-md); }
        .header-row, .panel-topline { display: flex; align-items: flex-start; gap: var(--gem-space-2); }
        .panel-topline { align-items: center; margin-bottom: var(--gem-space-3); }
        .brand-mark { width: 24px; height: 24px; border-radius: var(--gem-radius-sm); background: var(--gem-color-primary-tint); color: var(--gem-color-primary); display: inline-flex; align-items: center; justify-content: center; font: 750 12px/1 var(--gem-font-body); flex: 0 0 auto; }
        .eyebrow { margin: 0 0 4px; font-size: 11px; line-height: 1.2; text-transform: uppercase; letter-spacing: 0; color: var(--gem-color-muted); font-weight: 700; }
        h3 { margin: 0 28px var(--gem-space-3) 0; font-family: var(--gem-font-heading); font-size: 17px; line-height: 1.28; font-weight: 750; color: var(--gem-color-text); letter-spacing: 0; }
        p { margin: 0 0 var(--gem-space-3); font-size: 14px; line-height: 1.5; color: var(--gem-color-text); }
        .chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 var(--gem-space-4) 32px; }
        .chips button { appearance: none; display: inline-flex; padding: 5px 10px; border: 1px solid var(--gem-color-border-subdued); border-radius: 999px; background: var(--gem-color-surface); color: var(--gem-color-text); font: 700 11px/1.2 var(--gem-font-body); white-space: nowrap; cursor: pointer; transition: background var(--gem-transition-fast), border-color var(--gem-transition-fast), color var(--gem-transition-fast); }
        .chips button:hover { background: var(--gem-color-surface-alt); border-color: var(--gem-color-primary); color: var(--gem-color-primary); }
        .primary, .option { appearance: none; border-radius: var(--gem-radius-sm); cursor: pointer; font: 700 14px/1.2 var(--gem-font-body); min-height: 38px; transition: background var(--gem-transition-fast), border-color var(--gem-transition-fast), color var(--gem-transition-fast), box-shadow var(--gem-transition-fast); }
        .primary { display: inline-flex; align-items: center; justify-content: center; gap: 8px; border: 0; background: var(--gem-color-primary); color: white; padding: 10px 14px; }
        .primary:hover { background: var(--gem-color-primary-hover); }
        .primary:focus-visible, .option:focus-visible, .icon-button:focus-visible, .secondary:focus-visible, .chips button:focus-visible { outline: 2px solid var(--gem-color-info); outline-offset: 2px; }
        .primary svg { width: 15px; height: 15px; fill: currentColor; }
        .primary.link { text-decoration: none; }
        .secondary { display: inline-flex; margin-left: var(--gem-space-3); color: var(--gem-color-primary); font-size: 14px; font-weight: 700; text-decoration: none; }
        .followup { display: grid; gap: var(--gem-space-2); margin-top: var(--gem-space-4); padding-top: var(--gem-space-4); border-top: 1px solid var(--gem-color-border); }
        .followup label { color: var(--gem-color-muted); font: 750 12px/1.2 var(--gem-font-body); }
        .followup-row { display: grid; grid-template-columns: minmax(0, 1fr) 38px; gap: var(--gem-space-2); }
        .followup input { width: 100%; min-height: 38px; border: 1px solid var(--gem-color-border-subdued); border-radius: var(--gem-radius-sm); padding: 0 10px; color: var(--gem-color-text); font: 500 13px/1.2 var(--gem-font-body); }
        .followup input:focus-visible { outline: 2px solid var(--gem-color-info); outline-offset: 2px; }
        .followup-row button { width: 38px; min-height: 38px; border: 0; border-radius: var(--gem-radius-sm); background: var(--gem-color-primary); color: #fff; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
        .followup-row button:hover { background: var(--gem-color-primary-hover); }
        .followup-row button svg { width: 17px; height: 17px; fill: currentColor; }
        .followup-answer { border: 1px solid var(--gem-color-border); border-radius: var(--gem-radius-sm); background: var(--gem-color-surface-alt); padding: 10px; }
        .followup-answer p:last-child { margin-bottom: 0; }
        .answer-label { margin-bottom: 4px; color: var(--gem-color-primary); font-size: 11px; font-weight: 800; text-transform: uppercase; }
        .experience { display: grid; gap: var(--gem-space-3); margin-bottom: var(--gem-space-3); }
        .experience-section { border: 1px solid var(--gem-color-border); border-radius: var(--gem-radius-sm); background: var(--gem-color-surface); padding: 10px; }
        .experience-section h4 { margin: 0 0 6px; color: var(--gem-color-text); font: 750 13px/1.25 var(--gem-font-heading); letter-spacing: 0; }
        .experience-section p { margin-bottom: 0; font-size: 13px; }
        .experience-section.proof { border-left: 3px solid var(--gem-color-info); }
        .experience-section.product { background: var(--gem-color-surface-alt); }
        .experience-section.metric { display: grid; gap: 3px; }
        .metric-value { color: var(--gem-color-primary); font-size: 18px; font-weight: 800; line-height: 1.1; }
        .product-meta { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 0; color: var(--gem-color-muted); font-size: 12px; font-weight: 700; }
        .product-link { display: inline-flex; margin-top: 8px; color: var(--gem-color-primary); font-size: 13px; font-weight: 750; text-decoration: none; }
        .comparison-table { display: grid; border: 1px solid var(--gem-color-border); border-radius: var(--gem-radius-sm); overflow: hidden; }
        .comparison-row { display: grid; grid-template-columns: minmax(82px, 0.8fr) minmax(0, 1.6fr); gap: 8px; padding: 8px 10px; border-top: 1px solid var(--gem-color-border); font-size: 12px; line-height: 1.35; }
        .comparison-row:first-child { border-top: 0; }
        .comparison-label { color: var(--gem-color-muted); font-weight: 800; }
        .comparison-value { color: var(--gem-color-text); font-weight: 700; }
        .comparison-detail { display: block; color: var(--gem-color-muted); font-weight: 500; margin-top: 2px; }
        .timeline-list { display: grid; gap: 8px; }
        .timeline-item { border-top: 1px solid var(--gem-color-border); padding-top: 8px; }
        .timeline-item:first-child { border-top: 0; padding-top: 0; }
        .timeline-label { display: block; color: var(--gem-color-primary); font-size: 12px; font-weight: 850; margin-bottom: 3px; }
        .options { display: grid; gap: var(--gem-space-2); }
        .option { width: 100%; background: var(--gem-color-surface); color: var(--gem-color-text); border: 1px solid var(--gem-color-border); text-align: left; padding: 10px 12px; }
        .option:hover { background: var(--gem-color-surface-alt); border-color: var(--gem-color-primary); color: var(--gem-color-primary); }
        .icon-button { margin-left: auto; width: 28px; height: 28px; border: 0; border-radius: var(--gem-radius-sm); background: transparent; color: var(--gem-color-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
        .icon-button:hover { background: var(--gem-color-surface-alt); color: var(--gem-color-text); }
        .icon-button svg { width: 18px; height: 18px; fill: currentColor; }
        .step-count { margin-left: auto; color: var(--gem-color-muted); font-size: 12px; font-weight: 700; }
        .caution { background: var(--gem-color-surface-alt); border-left: 3px solid var(--gem-color-warning); border-radius: var(--gem-radius-sm); padding: 9px 10px; font-size: 13px; color: var(--gem-color-text); }
        @media (max-width: 640px) { .drawer { left: 10px; right: 10px; bottom: 10px; max-width: none; } .card { margin: 12px auto; } .chips { margin-left: 0; } }
      </style>
    `;
  }
}

function findAnchor(trigger: TriggerConfig): Element | null {
  const reservedSlot =
    document.querySelector(`[data-gemsales-slot="${cssEscape(trigger.id)}"]`) ??
    document.querySelector(`[data-gemsales-runtime-slot="${cssEscape(trigger.runtimeSlotId)}"]`) ??
    document.querySelector(`[data-gemsales-slot="${cssEscape(trigger.runtimeSlotId)}"]`);
  if (reservedSlot) return reservedSlot;

  for (const selector of trigger.anchorSelectors) {
    const anchor = document.querySelector(selector);
    if (anchor) return anchor;
  }

  return document.querySelector("main") ?? document.body;
}

function cssEscape(value: string): string {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : value.replace(/"/g, '\\"');
}

function isReservedSlot(anchor: Element, trigger: TriggerConfig): boolean {
  return (
    anchor.getAttribute("data-gemsales-runtime-slot") === trigger.runtimeSlotId ||
    anchor.getAttribute("data-gemsales-slot") === trigger.runtimeSlotId ||
    anchor.getAttribute("data-gemsales-slot") === trigger.id
  );
}

function supportsDynamicPage(experience: GeneratedExperience): boolean {
  return EXPERIENCE_TEMPLATES[experience.templateId]?.surfaces.includes("dynamic_page") ?? false;
}

/**
 * Relative on purpose: on a merchant domain it is meant to resolve through a Shopify app proxy
 * (`prefix = "apps"`, `subpath = "gemsales"`). Checked 2026-10-06 before renaming it from
 * `/apps/gemchat`: no app proxy with subpath `gemchat` is configured anywhere in the fleet (the only
 * `[app_proxy]` block, gemtheme `apps/shopify/shopify.app.example.toml`, uses subpath `gemtheme` with a
 * placeholder URL), so no store resolved the old path either. Only this repo's own server answers it,
 * and that server still serves `/apps/gemchat/*`. If an app proxy is ever configured, its subpath must
 * match this path.
 */
function buildExperienceGuideUrl(flowId: string, answers: Record<string, string>): string {
  return buildExperiencePageUrl(`/apps/gemsales/guide/${flowId}`, { flowId, answers });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function formatTopic(topic: string): string {
  return topic.replaceAll("_", " ");
}
